import { createContext, useContext, useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import api, {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  onUnauthorized,
  isAccountUnavailableError,
} from '../lib/api.js'

const AuthContext = createContext()

const REFERRAL_STORAGE_PREFIX = 'veohmo_referral_code:'


const getStoredReferralCode = (userId) => {
  if (!userId) return null
  return localStorage.getItem(`${REFERRAL_STORAGE_PREFIX}${userId}`)
}

const storeReferralCode = (userId, code) => {
  if (!userId || !code) return
  localStorage.setItem(`${REFERRAL_STORAGE_PREFIX}${userId}`, code)
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onUnauthorized(() => {
      setUser((prev) => {
        if (prev) {
          toast.error('Session expired. Please login again.')
        }
        return null
      })
      setLoading(false)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    checkAuth()
  }, [])

  const fetchProfile = async () => {
    const { data: profile } = await api.get('/user/profile')
    if (profile?.referralCode) {
      storeReferralCode(profile.id, profile.referralCode)
      return profile
    }

    const storedCode = getStoredReferralCode(profile?.id)
    if (storedCode && !profile?.referralCode) {
      return { ...profile, referralCode: storedCode }
    }
    return profile
  }

  const checkAuth = async () => {
    try {
      const token = getAuthToken()
      if (token) {
        const profile = await fetchProfile()
        setUser(profile)
      }
    } catch (error) {
      clearAuthToken()
      if (isAccountUnavailableError(error)) {
        toast.error('Account not available')
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password })
      const { token } = data || {}
      if (!token) {
        throw new Error('No token received')
      }
      setAuthToken(token)
      const profile = await fetchProfile()
      setUser(profile)
      toast.success('Login successful!')
      return { success: true }
    } catch (error) {
      if (error.response?.status === 429) {
        // The global API interceptor already surfaces a friendly rate-limit notice;
        // avoid showing a duplicate/ugly generic error here.
        return { success: false, rateLimited: true }
      }
      if (isAccountUnavailableError(error)) {
        clearAuthToken()
        setUser(null)
        toast.error('Account not available')
        return { success: false, accountUnavailable: true, message: 'Account not available' }
      }
      // Check if email is not verified
      if (error.response?.data?.error === 'EMAIL_NOT_VERIFIED') {
        const email = error.response?.data?.email || email
        return { 
          success: false, 
          emailNotVerified: true, 
          email,
          message: error.response?.data?.message || 'Please verify your email address before logging in'
        }
      }
      
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Login failed'
      toast.error(message)
      return { success: false }
    }
  }

  const register = async (userData) => {
    try {
      // If userData is FormData, axios will automatically set Content-Type to multipart/form-data with boundary
      // Don't manually set Content-Type header for FormData - axios needs to set it with boundary
      // For regular objects, axios will use application/json
      const { data } = await api.post('/auth/register', userData)
      const { token, user } = data || {}
      
      // If no token is returned, registration was successful but email verification is required
      // Backend returns { user } when email verification is needed
      if (!token) {
        // Show success message about email verification
        toast.success('Registration successful! Please check your email to verify your account.')
        // Return success with verification flag - don't set token or user
        return { success: true, requiresVerification: true, user: user || data?.user || data }
      }
      
      // If token is returned, set it and log user in (legacy behavior)
      setAuthToken(token)
      const profile = await fetchProfile()
      setUser(profile)
      toast.success('Registration successful!')
      return { success: true, requiresVerification: false }
    } catch (error) {
      if (error.response?.status === 429) {
        // The global API interceptor already surfaces a friendly rate-limit notice.
        return { success: false, rateLimited: true }
      }
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg || err.message).join(', ')
        toast.error(errorMessages)
      } else {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Registration failed'
      toast.error(message)
      }
      return { success: false }
    }
  }

  const logout = () => {
    clearAuthToken()
    setUser(null)
    toast.success('Logged out successfully')
  }

  const updateProfile = async (profileData) => {
    try {
      const { data: updated } = await api.put('/user/profile', profileData)
      const userId = user?.id || updated?.id

      if (updated?.referralCode && userId) {
        storeReferralCode(userId, updated.referralCode)
      }
      const storedCode = userId ? getStoredReferralCode(userId) : null

      setUser((prev) => {
        return {
          ...prev,
          ...updated,
          referralCode: updated?.referralCode || storedCode || prev?.referralCode || null,
        }
      })
      toast.success('Profile updated successfully!')
      return true
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile')
      return false
    }
  }

  const generateReferralCode = async () => {
    if (!user) {
      toast.error('You need to be logged in to generate a referral code.')
      return null
    }

    if (user.referralCode) {
      toast.success('You already have a referral code.')
      return user.referralCode
    }

    try {
      const { data } = await api.post('/user/referral-code')
      const { referralCode } = data || {}

      if (!referralCode) {
        throw new Error('No referral code received')
      }

      // Store in local storage as backup
      if (user.id) {
        storeReferralCode(user.id, referralCode)
      }

      // Update user state
      setUser((prev) => (prev ? { ...prev, referralCode } : prev))
      toast.success('Referral code generated!')
      return referralCode
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to generate referral code'
      toast.error(errorMessage)
      return null
    }
  }

  const refreshUser = async () => {
    try {
      const profile = await fetchProfile()
      setUser(profile)
      return profile
    } catch (error) {
      console.error('Failed to refresh user:', error)
      return null
    }
  }

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    generateReferralCode,
    refreshUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 